import React, { useState, useEffect } from 'react';
import { Mail, Phone, Menu, X } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { brand, nav } from '../mock';
import Logo from './Logo';

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // hash links should scroll on the homepage, or route home + scroll from elsewhere
  const hashHref = (href) => (pathname === '/' ? href : `/${href}`);
  const isRoute = (href) => href.startsWith('/');

  const NavLink = ({ item, className, onClick }) => {
    if (item.download) {
      return (
        <a href={item.href} download target="_blank" rel="noopener noreferrer" className={className} onClick={onClick}>
          {item.label}
        </a>
      );
    }
    return isRoute(item.href) ? (
      <Link to={item.href} className={className} onClick={onClick}>{item.label}</Link>
    ) : (
      <a href={hashHref(item.href)} className={className} onClick={onClick}>{item.label}</a>
    );
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50">
      {/* top row: logo left, combined contact + nav box right */}
      <div className="px-5 md:px-10 pt-5">
      <div className="max-w-[1400px] mx-auto flex items-start justify-between">
        <Link to="/" className="block">
          <Logo className="h-7 md:h-8" />
        </Link>

        {/* combined contact + nav box, aligned right */}
        <div className="hidden md:flex flex-col items-end gap-3">
          <div className="hidden lg:flex items-center gap-7 text-sm text-white/90 pr-2">
            <a href={`mailto:${brand.email}`} className="flex items-center gap-2 hover:text-yellow transition-colors">
              <Mail className="w-4 h-4" strokeWidth={2} />
              {brand.email}
            </a>
            <a href={brand.phoneHref} className="flex items-center gap-2 hover:text-yellow transition-colors">
              <Phone className="w-4 h-4" strokeWidth={2} />
              {brand.phone}
            </a>
          </div>
          <nav
            className={`flex items-center gap-1 rounded-[10px] pl-8 pr-1.5 py-1.5 transition-all duration-300 bg-white/95 ${
              scrolled ? 'shadow-lg shadow-black/30' : ''
            }`}
          >
          {nav.map((item) => (
            <NavLink
              key={item.label}
              item={item}
              className="flex items-center gap-1 px-5 py-2.5 rounded-[10px] text-[15px] font-bold transition-colors text-[#262629] hover:text-[#ff7a1a]"
            />
          ))}
          <Link
            to="/contact"
            className="ml-2 bg-jaw-yellow text-[#262629] font-semibold text-[15px] px-7 py-3 rounded-[10px] hover:bg-[#e8660a] transition-colors"
          >
            Get in touch
          </Link>
        </nav>
        </div>

        {/* mobile toggle */}
        <button
          onClick={() => setMobileOpen(true)}
          className="md:hidden absolute top-6 right-5 bg-white/95 text-[#262629] p-2.5 rounded-full"
          aria-label="Open menu"
        >
          <Menu className="w-6 h-6" />
        </button>
      </div>
      </div>

      {/* mobile drawer */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-[60] bg-[#262629] p-6 flex flex-col">
          <div className="flex items-center justify-between mb-10">
            <Logo className="h-8" />
            <button onClick={() => setMobileOpen(false)} aria-label="Close menu">
              <X className="w-7 h-7 text-white" />
            </button>
          </div>
          <div className="flex flex-col gap-2">
            {nav.map((item) => (
              <NavLink
                key={item.label}
                item={item}
                onClick={() => setMobileOpen(false)}
                className="font-display text-3xl py-2 text-white hover:text-yellow transition-colors"
              />
            ))}
            <Link
              to="/contact"
              onClick={() => setMobileOpen(false)}
              className="mt-6 bg-jaw-yellow text-[#262629] font-semibold text-center px-7 py-4 rounded-[10px]"
            >
              Get in touch
            </Link>
            <div className="mt-8 text-white/80 flex flex-col gap-3">
              <a href={`mailto:${brand.email}`} className="flex items-center gap-2"><Mail className="w-4 h-4" />{brand.email}</a>
              <a href={brand.phoneHref} className="flex items-center gap-2"><Phone className="w-4 h-4" />{brand.phone}</a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
